/**
 * AES-GCM encryption helpers for E2EE (device-bound model).
 *
 * Format v3 (current): {v:3, iv, ct}     — encrypted with a family key (CryptoKey)
 *                                           that was distributed via ECDH wrap.
 *
 * Format v1/v2 (legacy, password-based PBKDF2): {iv, encrypted, salt, version}
 *   — NOT supported anymore. After E2EE rewrite the old `familyKeys` table
 *   was wiped (see `convex/migration.ts:wipeOldE2EE`). Old plaintext stays as
 *   plaintext until the migration UI re-encrypts it under the new model.
 */

export interface EncryptedPayload {
  v: 3;
  iv: string; // base64
  ct: string; // base64
}

export const FAMILY_KEY_ALGO: AesKeyAlgorithm = { name: "AES-GCM", length: 256 };

export const bytesToBase64 = (bytes: Uint8Array | ArrayBuffer): string => {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = "";
  for (let i = 0; i < arr.byteLength; i++) {
    binary += String.fromCharCode(arr[i]);
  }
  return btoa(binary);
};

export const base64ToBytes = (b64: string): Uint8Array => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

/**
 * Encrypts a string with an AES-GCM CryptoKey.
 * Returns a serialized JSON payload that can be stored as-is.
 */
export const aesEncrypt = async (plaintext: string, key: CryptoKey): Promise<string> => {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(plaintext);
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data);
  const payload: EncryptedPayload = {
    v: 3,
    iv: bytesToBase64(iv),
    ct: bytesToBase64(ct),
  };
  return JSON.stringify(payload);
};

/**
 * Decrypts a v3 payload string with an AES-GCM CryptoKey.
 * Returns null if input isn't a valid v3 payload.
 */
export const aesDecrypt = async (serialized: string, key: CryptoKey): Promise<string | null> => {
  let payload: EncryptedPayload;
  try {
    const parsed = JSON.parse(serialized);
    if (parsed?.v !== 3 || typeof parsed.iv !== "string" || typeof parsed.ct !== "string") {
      return null;
    }
    payload = parsed as EncryptedPayload;
  } catch {
    return null;
  }

  const iv = base64ToBytes(payload.iv);
  const ct = base64ToBytes(payload.ct);
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
  return new TextDecoder().decode(pt);
};

/**
 * Cheap check: does this string look like an encrypted payload?
 * Used by display layer to decide whether to attempt decrypt.
 */
export const isEncryptedPayload = (value: string): boolean => {
  if (!value || value[0] !== "{") return false;
  try {
    const p = JSON.parse(value);
    return p?.v === 3 && typeof p.iv === "string" && typeof p.ct === "string";
  } catch {
    return false;
  }
};
